import React, { useState, useEffect } from 'react';
import { useDispute } from '../../../hooks/admin/useDispute';
import { useDisputeTimeline } from '../../../hooks/admin/useDisputeTimeline';
import { useModerateDispute } from '../../../hooks/admin/useModerateDispute';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Textarea } from '../../ui/textarea';
import { X, FileText, MessageSquare, History, DollarSign, Gavel, StickyNote, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { RefundSlider } from './RefundSlider';
import { FixScheduler } from './FixScheduler';
import { DecisionConfirmModal } from './DecisionConfirmModal';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '../../../hooks/use-toast';

export function DisputeDetailDrawer({ disputeId, isOpen, onClose }) {
  const { data: dispute, isLoading, error } = useDispute(disputeId);
  const { data: timeline } = useDisputeTimeline(disputeId);
  const moderateMutation = useModerateDispute();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('summary');
  const [decision, setDecision] = useState(null);
  const [refundPercent, setRefundPercent] = useState(100);
  const [fixDueDate, setFixDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setDecision(null);
      setRefundPercent(100);
      setFixDueDate(null);
      setNotes('');
      setShowConfirmModal(false);
      setActiveTab('summary');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDecisionClick = (decisionType) => {
    setDecision(decisionType);
    setShowConfirmModal(true);
  };

  const handleConfirmDecision = async () => {
    if (!notes.trim()) {
      toast({ title: 'Error', description: 'Notes are required for all decisions', variant: 'destructive' });
      return;
    }

    try {
      await moderateMutation.mutateAsync({
        disputeId,
        decision,
        percent: decision === 'refund' ? refundPercent : undefined,
        newDueAt: decision === 'fix' ? fixDueDate : undefined,
        notes,
      });

      toast({ title: 'Success', description: 'Decision executed successfully' });
      setShowConfirmModal(false);
      onClose();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to execute decision',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'negotiating': return 'bg-yellow-100 text-yellow-800';
      case 'needs_moderation': return 'bg-orange-100 text-orange-800';
      case 'escalated': return 'bg-red-100 text-red-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />

      <div className="fixed right-0 top-0 h-full w-[720px] max-w-[95vw] bg-white shadow-2xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Dispute Details</h2>
            {dispute && (
              <Badge className={getStatusBadgeColor(dispute.status)}>
                {dispute.status.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6">
          {isLoading && (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse h-24 bg-gray-100 rounded"></div>
              ))}
            </div>
          )}

          {error && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6">
                <p className="text-sm text-red-800">Failed to load dispute details. Please try again.</p>
              </CardContent>
            </Card>
          )}

          {dispute && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-3 lg:grid-cols-6 w-full mb-6">
                <TabsTrigger value="summary" className="gap-1">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Summary</span>
                </TabsTrigger>
                <TabsTrigger value="evidence" className="gap-1">
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Evidence</span>
                </TabsTrigger>
                <TabsTrigger value="timeline" className="gap-1">
                  <History className="h-4 w-4" />
                  <span className="hidden sm:inline">Timeline</span>
                </TabsTrigger>
                <TabsTrigger value="finance" className="gap-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="hidden sm:inline">Finance</span>
                </TabsTrigger>
                <TabsTrigger value="actions" className="gap-1">
                  <Gavel className="h-4 w-4" />
                  <span className="hidden sm:inline">Actions</span>
                </TabsTrigger>
                <TabsTrigger value="notes" className="gap-1">
                  <StickyNote className="h-4 w-4" />
                  <span className="hidden sm:inline">Notes</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="summary">
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Dispute Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Dispute ID</p>
                          <p className="text-sm font-mono">{dispute.id}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Order ID</p>
                          <p className="text-sm font-mono">{dispute.order_id || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Reason</p>
                          <p className="text-sm capitalize">{dispute.reason?.replace(/_/g, ' ') || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Created</p>
                          <p className="text-sm">{formatDistanceToNow(new Date(dispute.created_at), { addSuffix: true })}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Category</p>
                          <p className="text-sm">{dispute.category || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Auto-approval</p>
                          <Badge variant={dispute.auto_approval_paused ? 'destructive' : 'default'}>
                            {dispute.auto_approval_paused ? 'Paused' : 'Active'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Parties</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Buyer</p>
                        <p className="text-sm font-medium">{dispute.buyer_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{dispute.buyer_email || ''}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Seller</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{dispute.seller_name || 'Unknown'}</p>
                          {dispute.seller_tier !== null && dispute.seller_tier !== undefined && (
                            <Badge variant="outline">T{dispute.seller_tier}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{dispute.seller_email || ''}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Order Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Order Total</span>
                        <span className="text-sm font-semibold">${((dispute.order_total_cents || 0) / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Buyer Fee</span>
                        <span className="text-sm">${((dispute.buyer_fee_cents || 0) / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Seller Payout (Nominal)</span>
                        <span className="text-sm">${((dispute.seller_payout_cents || 0) / 100).toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="evidence">
                <Card>
                  <CardHeader>
                    <CardTitle>Evidence & Attachments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Evidence files and attachments will be displayed here once the backend endpoints are connected.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="timeline">
                <Card>
                  <CardHeader>
                    <CardTitle>Negotiation Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {timeline && timeline.events && timeline.events.length > 0 ? (
                      <div className="space-y-4">
                        {timeline.events.map((event, idx) => (
                          <div key={idx} className="flex gap-4 pb-4 border-b last:border-0">
                            <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{event.type}</p>
                              <p className="text-xs text-gray-600">{event.actor || 'System'}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDistanceToNow(new Date(event.ts), { addSuffix: true })}
                              </p>
                              {event.details && <p className="text-sm text-gray-700 mt-2">{event.details}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">No timeline events yet.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="finance">
                <Card>
                  <CardHeader>
                    <CardTitle>Financial Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-900">
                          <strong>Note:</strong> This is a preview. Final execution amounts come from the server.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Order Total</span>
                          <span className="text-sm font-semibold">${((dispute.order_total_cents || 0) / 100).toFixed(2)}</span>
                        </div>

                        {decision === 'refund' && (
                          <>
                            <div className="flex justify-between text-red-600">
                              <span className="text-sm font-medium">Refund to Buyer ({refundPercent}%)</span>
                              <span className="text-sm font-semibold">
                                -${((dispute.order_total_cents || 0) * refundPercent / 10000).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between text-green-600">
                              <span className="text-sm font-medium">Seller Net</span>
                              <span className="text-sm font-semibold">
                                ${(Math.max(0, (dispute.order_total_cents || 0) * (100 - refundPercent) / 10000)).toFixed(2)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {dispute.stripe_mode === 'test' && (
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                          Stripe: TEST
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="actions">
                {dispute.status === 'resolved' ? (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                        <div>
                          <p className="font-semibold text-green-900">Dispute Resolved</p>
                          <p className="text-sm text-green-800 mt-1">
                            This dispute has been resolved. No further action required.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Award Buyer (Refund)</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <RefundSlider value={refundPercent} onChange={setRefundPercent} />
                        
                        <div className="pt-4 border-t">
                          <label className="text-sm font-medium text-gray-700">Decision Notes (Required)</label>
                          <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Explain your decision..."
                            className="mt-2"
                            rows={3}
                          />
                        </div>

                        <Button
                          onClick={() => handleDecisionClick('refund')}
                          disabled={moderateMutation.isPending || !notes.trim()}
                          className="w-full"
                        >
                          Issue Refund
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Award Seller (Reject Dispute)</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Reject the dispute and release payment to seller.
                        </p>

                        <div>
                          <label className="text-sm font-medium text-gray-700">Decision Notes (Required)</label>
                          <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Explain why the dispute is rejected..."
                            className="mt-2"
                            rows={3}
                          />
                        </div>

                        <Button
                          variant="outline"
                          onClick={() => handleDecisionClick('reject')}
                          disabled={moderateMutation.isPending || !notes.trim()}
                          className="w-full"
                        >
                          Reject Dispute
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Fix & Re-deliver</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FixScheduler value={fixDueDate} onChange={setFixDueDate} />
                        
                        <div className="pt-4 border-t">
                          <label className="text-sm font-medium text-gray-700">Instructions for Seller (Required)</label>
                          <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Describe what needs to be fixed..."
                            className="mt-2"
                            rows={3}
                          />
                        </div>

                        <Button
                          variant="secondary"
                          onClick={() => handleDecisionClick('fix')}
                          disabled={moderateMutation.isPending || !notes.trim() || !fixDueDate}
                          className="w-full"
                        >
                          Schedule Fix
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="notes">
                <Card>
                  <CardHeader>
                    <CardTitle>Internal Moderator Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Internal notes and moderation flags will be displayed here.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {showConfirmModal && dispute && (
        <DecisionConfirmModal
          isOpen={showConfirmModal}
          decision={decision}
          refundPercent={refundPercent}
          orderTotal={dispute.order_total_cents || 0}
          isTestMode={dispute.stripe_mode === 'test'}
          isPending={moderateMutation.isPending}
          onConfirm={handleConfirmDecision}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
}
